import { fromEvent, debounceTime, tap, throttleTime } from 'rxjs';

import { constants } from '@config';
import { getLinkedTextAreaId, getLinkedTextAreaPageId } from 'src/helpers/text-overflow-links';
import * as TextStylingService from 'src/services/text-styling';
import { convertPixelToRem } from 'src/utils/toolbelt';

import tableQuery from '../table/table-query';

module.exports = function (S) {
  const canvas = S.canvas;
  const curConfig = S.curConfig;

  const NS = svgedit.NS;

  let mouseMoving;

  let currentBold;
  let currentItalic;
  let currentUnderline;

  // Current text elements
  let currentTextInstance;
  let currentTextInputElement; // hidden input element
  let currentTextInputElementLastFocus;
  let currentTextContent; // value from currentTextInputElement

  // Cursor
  let textCursor;
  let textCursorAnimation;

  // pointer cursor
  let pStart;

  // Selection
  let selectionBlock;
  let isMouseDown; // used to determine if mouse down and can we select a text by mouse
  let selectionInitialIndex = 0;

  let matrix;

  // Right-to-left text
  let isRtl;

  const $textEdit = $('#text-edit');
  let $textEditCharacterCount = null;

  let timesClickedOnElement = 0;

  let updateTextEventObserver;
  let saveTextEventObserver;
  let shouldTriggerTextUpdate;

  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);
  const shortcutKey = isMac ? 'metaKey' : 'ctrlKey';

  // Init mobile text edit
  if ($textEdit.length) {
    function closeTextArea () {
      toggleHeaderButtons(false);
      clear(true);
      canvas.setMode('select');
      $textEdit.removeClass('in');
      $textEdit.find('.value').off('input').off('blur');
      INKIVE.Tool.enableUI();
      document.querySelectorAll('#svgcanvas, #svgcontent g.main>.element').forEach((e) => {
        e.style.pointerEvents = null;
        e.getAttribute('style') || e.removeAttribute('style');
      });

      const textarea = document.querySelector('textarea.value');

      textarea.removeEventListener('keyup', handleRtlTextarea);
      textarea.style.direction = '';
      textarea.blur();

      const editor = document.getElementById('ik-editor');
      editor.classList.remove('disable-click');
    }

    $textEdit.find('.text-edit-cancel').click(closeTextArea);

    $textEdit.find('form').on('submit', function (event) {
      event.preventDefault();
      if (!currentTextInstance) {
        return;
      }
      const renderText = $textEdit.find('.value').val()
        .replace(' ', String.fromCharCode(160));
      const textLength = removeLineBreaks(renderText).length;
      const permissions = INKIVE.RestrictionsFacade.getAllElementPermissions(currentTextInstance.dom.id);
      const charNumberPermission = permissions && permissions[0] && permissions[0].character_number;

      event.preventDefault();

      if (charNumberPermission) {
        if (textLength < charNumberPermission.min || textLength > charNumberPermission.max) {
          canvas.call('advice.show', 'restriction-text-character-number-range', {
            min: charNumberPermission.min,
            max: charNumberPermission.max
          });
          return;
        }
      }

      currentTextInstance.text.setValues({ direction: document.querySelector('textarea.value').style.direction });

      const spaceRegex = / /g;
      const difference = INKIVE.Tool.calculateStringContentDifference(
        currentTextInstance.text.textContent.replace(spaceRegex, constants.text.noBreakSpace),
        renderText.replace(spaceRegex, constants.text.noBreakSpace)
      );

      const newLetters = TextStylingService.getRealContentLength(renderText) - TextStylingService.getRealContentLength(currentTextInstance.text.textContent);
      const cursorPosition = newLetters >= 0
        ? difference.position + newLetters
        : difference.position;

      if (currentTextInstance.child_text) {
        INKIVE.DesignFacade.set({
          textarea: currentTextInstance,
          text_overflow: renderText,
          force_redraw: true,
          typing: true,
          cursor_position: cursorPosition
        });
      } else {
        INKIVE.DesignFacade.set({
          textarea: currentTextInstance,
          text_content: renderText,
          force_redraw: true,
          typing: true,
          cursor_position: cursorPosition
        });
      }

      closeTextArea();
    });
  }

  function toggleHeaderButtons (disable) {
    const $header = $('#header');
    $header.find('.btn').each(function (index, elem) {
      disable && $(elem).addClass('disabled');
      disable || $(elem).removeClass('disabled');
    });
    const highlightFooter = function () {
      $('#text-edit').find('.footer .btn').animate({
        'background-color': '#414A4B',
        color: '#FFDD17'
      }, 250).animate({
        'background-color': '#FFDD17',
        color: '#414A4B'
      }, 250);
    };
    disable && $header.find('.right-menu').on('click', highlightFooter);
    disable || $header.find('.right-menu').off('click', highlightFooter);
  }

  function showTextForm (opts) {
    const conflictSpace = new RegExp(String.fromCharCode(160), 'g');
    const cleanText = currentTextInputElement.value.replace(conflictSpace, ' ');
    const textLenght = removeLineBreaks(cleanText).length;
    const $textArea = $textEdit.find('.value');
    const permissions = INKIVE.RestrictionsFacade.getAllElementPermissions(opts.elem.getAttribute('id'));
    const editor = document.getElementById('ik-editor');
    editor.classList.add('disable-click');

    if (permissions && permissions[0] && permissions[0].character_number) {
      const charNumber = permissions[0].character_number;

      $textEditCharacterCount && $textEditCharacterCount.remove();
      $textEditCharacterCount = $('<div id="text-edit-character-count" ></div>');
      $textEditCharacterCount.html(textLenght + '/' + charNumber.max);

      $textArea.css('padding-right', 50);
      $textArea.after($textEditCharacterCount);
      $textArea.off('input').on('input', function () {
        const textValue = removeLineBreaks($textArea.val());
        const textLenght = textValue.length;

        if (textLenght > charNumber.max) {
          INKIVE.Tool.blinkScreen($textEditCharacterCount);
        }

        $textEditCharacterCount.html(textLenght + '/' + charNumber.max);
      });
    }

    INKIVE.Tool.disableUI();
    document.querySelectorAll('#svgcanvas, #svgcontent g.main>.element').forEach((e) => {
      e.style.pointerEvents = 'none';
    });
    $textArea.val(cleanText);
    $textEdit.addClass('in');
    $textArea.focus();

    const textarea = document.querySelector('textarea.value');

    textarea.style.direction = /[\u0591-\u07FF]/.test($textArea[0].value)
      ? 'rtl'
      : null;
    textarea.addEventListener('keyup', handleRtlTextarea);
  }

  function removeLineBreaks (value) {
    return value.replace(/(\r\n|\n|\r)/g, '');
  }

  function handleRtlTextarea (event) {
    this.style.direction = /[\u0591-\u07FF]/.test(event.currentTarget.value) ? 'rtl' : null;
  }

  function initCache (elem) {
    // Find text element and init global variables
    if (elem && elem.tagName === 'g' && $(elem).is('.textarea')) {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(elem, false);
      matrix = svgedit.math.getMatrix(elem);
      if (!currentTextInputElement) {
        currentTextInputElement = $(curConfig.initText.input).get(0);
        $(currentTextInputElement).blur(function () {
          currentTextInputElementLastFocus = this;
        });
      }

      currentTextInputElement.value = currentTextInstance.text.textContent;
      currentTextContent = currentTextInputElement.value;
    }
  }

  function initWeb () {
    const $currentTextInputElement = $(currentTextInputElement).get(0);
    let currentCursorPosition = 0;
    let deleteListBlankLine = false;
    let selectionStartBeforeDelete = 0;
    let selectionEndBeforeDelete = 0;

    const tabKeyCode = 9;
    const upKeyCode = 38;
    const downKeyCode = 40;
    const leftKeyCode = 37;
    const rightKeyCode = 39;
    const backspaceCode = 8;
    const spaceCode = 32;
    const straightDoubleQuotes = constants.text.quoteCharacters.straightDoubleQuotes;
    const straightSingeQuote = constants.text.quoteCharacters.straightSingeQuote;
    const closingDoubleQuotes = constants.text.quoteCharacters.closingDoubleQuotes;
    const closingSingeQuote = constants.text.quoteCharacters.closingSingeQuote;

    function onInput (event, opts) {
      if (!currentTextInstance) return;
      opts = opts || {};
      $currentTextInputElement.style.direction = /[\u0591-\u07FF]/.test($currentTextInputElement.value) ? 'rtl' : null;

      if (event.which === tabKeyCode) {
        $currentTextInputElement.value = $currentTextInputElement.value.replace(/\t/g, '');
      }

      if (deleteListBlankLine) {
        $currentTextInputElement.value = currentTextContent;
        $currentTextInputElement.selectionStart = selectionStartBeforeDelete;
        $currentTextInputElement.selectionEnd = selectionEndBeforeDelete;
        shouldTriggerTextUpdate = true;
        deleteListBlankLine = false;

        // Since we already render the text on keydown when press delete,
        // we return here to avoid rendering the text twice
        return;
      } else if (event.which === spaceCode) {
        handleListShortcut();
        shouldTriggerTextUpdate = true;
      } else if (event.key === straightDoubleQuotes || event.key === straightSingeQuote) {
        const isDoubleQuote = event.key === straightDoubleQuotes;
        handleStraightQuote(isDoubleQuote);
      } else if (event.key === closingDoubleQuotes || event.key === closingSingeQuote) {
        const isDoubleQuote = event.key === closingDoubleQuotes;
        handleClosingQuote(isDoubleQuote);
      }

      const content = $currentTextInputElement.value;
      const initialStart = $currentTextInputElement.selectionStart;

      const textLength = removeLineBreaks(content).length;
      const permissions = INKIVE.RestrictionsFacade.getAllElementPermissions(currentTextInstance.dom.id);
      const charNumberPermission = permissions && permissions[0] && permissions[0].character_number;

      if (charNumberPermission) {
        if (textLength < charNumberPermission.min || textLength > charNumberPermission.max) {
          canvas.call('advice.show', 'restriction-text-character-number-range', {
            min: charNumberPermission.min,
            max: charNumberPermission.max
          });
          return;
        }
      }

      if (currentTextContent !== content) {
        shouldTriggerTextUpdate = true;
        currentTextContent = content;

        let selectionStart = $currentTextInputElement.selectionStart;
        if ((event.key === 'z' && event.ctrlKey) || event.inputType === 'historyUndo') {
          selectionStart = $currentTextInputElement.selectionEnd;
        }

        const forceRedraw = _.contains(['Enter', 'Backspace', 'Tab'], event.key) ||
          content.length < 200 || opts.forceRedraw;

        currentTextInstance.text.setValues({ direction: $currentTextInputElement.style.direction });

        const textAlignButton = document.querySelector('.btn[data-action="on_text-align"]');
        const handleRtl = textAlignButton && currentTextInstance.text.direction === 'rtl' && !textAlignButton.classList.contains('rtl-applied');
        const handleLtr = textAlignButton && currentTextInstance.text.direction !== 'rtl' && textAlignButton.classList.contains('rtl-applied');

        (handleRtl || handleLtr) && INKIVE.Text.Helpers.handleAlignmentIcon(handleRtl, handleLtr, textAlignButton);

        if (currentTextInstance.child_text || currentTextInstance.parent_text) {
          INKIVE.DesignFacade.set({
            skipHistory: true,
            textarea: currentTextInstance,
            text_overflow: content,
            force_redraw: forceRedraw,
            typing: true,
            cursor_position: selectionStart
          });

          currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
          currentTextInputElement.value = currentTextInstance.text.textContent;
          currentTextInputElement.selectionStart = selectionStart;
          currentTextContent = currentTextInputElement.value;
          currentTextInputElement.selectionEnd = selectionStart;
        } else {
          INKIVE.DesignFacade.set({
            skipHistory: true,
            textarea: currentTextInstance,
            text_content: content,
            force_redraw: forceRedraw,
            typing: true,
            cursor_position: selectionStart
          });
        }
      }

      try {
        updateSelection(initialStart, shouldTriggerTextUpdate);
      } catch {
        updateSelection(initialStart);
      }
    }

    function onKeyUp (event, opts) {
      if (![upKeyCode, downKeyCode, leftKeyCode, rightKeyCode].includes(event.which)) return;

      const initialStart = $currentTextInputElement.selectionStart;
      handleArrowKey(event);
      updateSelection(initialStart, false);
    }

    function updateTextElemSelection (lineArray, lineIndex, cursorLineIndex, textAnchor = 'start') {
      cursorLineIndex = cursorLineIndex > lineArray[lineIndex] - 1
        ? lineArray[lineIndex] - 1
        : cursorLineIndex;
      const cursorTextIndex = getCursorTextIndex(lineArray, lineIndex, cursorLineIndex, textAnchor);
      $currentTextInputElement.selectionStart = cursorTextIndex;
      $currentTextInputElement.selectionEnd = cursorTextIndex;
    }

    function replaceQuote (quote) {
      const contentLength = $currentTextInputElement.value.length;
      const initialSelectionStart = $currentTextInputElement.selectionStart;
      $currentTextInputElement.value =
        $currentTextInputElement.value.slice(0, $currentTextInputElement.selectionStart - quote.length) +
        quote +
        $currentTextInputElement.value.slice($currentTextInputElement.selectionStart, contentLength);
      $currentTextInputElement.selectionStart = initialSelectionStart;
      $currentTextInputElement.selectionEnd = initialSelectionStart;
    }

    function handleStraightQuote (isDoubleQuote) {
      const smartQuote = TextStylingService.getSmartQuote($currentTextInputElement.value, $currentTextInputElement.selectionStart, isDoubleQuote);
      if (!smartQuote) return;

      replaceQuote(smartQuote);
    }

    function handleClosingQuote (isDoubleQuote) {
      const straightQuote = isDoubleQuote ? straightDoubleQuotes : straightSingeQuote;
      replaceQuote(straightQuote);
    }

    function handleListShortcut () {
      const isBulletShortcut = TextStylingService.isListShortcut($currentTextInputElement.value, $currentTextInputElement.selectionStart, constants.text.bulletPoints.shortcut);
      const isOrderedShortcut = TextStylingService.isListShortcut($currentTextInputElement.value, $currentTextInputElement.selectionStart, constants.text.orderedList.shortcut);

      if (!isBulletShortcut && !isOrderedShortcut) return;

      const shortcut = isBulletShortcut ? constants.text.bulletPoints.shortcut : constants.text.orderedList.shortcut;
      const listType = isBulletShortcut ? 'bullets' : 'ordered';
      const contentLength = $currentTextInputElement.value.length;
      const initialSelectionStart = $currentTextInputElement.selectionStart;
      $currentTextInputElement.value = $currentTextInputElement.value.slice(0, $currentTextInputElement.selectionStart - shortcut.length) + $currentTextInputElement.value.slice($currentTextInputElement.selectionStart, contentLength);
      $currentTextInputElement.selectionStart = initialSelectionStart - shortcut.length;
      $currentTextInputElement.selectionEnd = $currentTextInputElement.selectionStart;
      currentTextInstance.text.setLineStyling($currentTextInputElement.selectionStart, $currentTextInputElement.selectionEnd, listType, 1);
    }

    function handleArrowKey (event) {
      const editingText = INKIVE.Tool.getInstance().editor.canvas.getSelectedElems()[0];
      const textAnchor = editingText.querySelector('g.text').getAttribute('text-anchor');
      const numOfLines = editingText.querySelectorAll('text').length;
      const lineArray = getLineArray(editingText, numOfLines);
      const relativeOutput = getCursorLineIndex(lineArray, currentCursorPosition, textAnchor);

      let isPressed = 0;
      let lineIndex = relativeOutput[0];
      const cursorLineIndex = relativeOutput[1];

      const isUpKey = event.which === upKeyCode;
      const isDownKey = event.which === downKeyCode;
      const isFirstLine = lineIndex <= 0;
      const isLastLine = lineIndex >= numOfLines - 1;

      if (isUpKey && !isFirstLine) {
        lineIndex -= 1;
        isPressed = 1;
      } else if (isDownKey && !isLastLine) {
        lineIndex += 1;
        isPressed = 1;
      }

      if (isPressed) {
        updateTextElemSelection(lineArray, lineIndex, cursorLineIndex, textAnchor);
      }
    }

    function handleTab () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.oldContent = currentTextInstance.text.textContent;
      currentTextInstance.text.increaseListLevel($currentTextInputElement.selectionStart, $currentTextInputElement.selectionEnd);
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    function handleShiftTab () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.oldContent = currentTextInstance.text.textContent;
      currentTextInstance.text.decreaseListLevel($currentTextInputElement.selectionStart, $currentTextInputElement.selectionEnd);
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    function handleDeleteList () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.oldContent = currentTextInstance.text.textContent;
      const lineStyling = currentTextInstance.text.getLineStyling(currentTextInputElement.selectionStart, currentTextInputElement.selectionEnd);

      const contentBeforeCursor = currentTextInstance.text.textContent?.slice?.(
        0,
        currentTextInputElement.selectionStart
      );

      const shouldDelete =
        contentBeforeCursor?.endsWith?.('\n') ||
        currentTextInputElement.selectionStart === 0;

      if (!shouldDelete) return;
      if (!lineStyling.orderedLevel && !lineStyling.bulletLevel) return;

      const listType = lineStyling.orderedLevel ? 'ordered' : 'bullets';
      deleteListBlankLine = true;
      selectionStartBeforeDelete = currentTextInputElement.selectionStart;
      selectionEndBeforeDelete = currentTextInputElement.selectionEnd;
      currentTextInstance.text.setLineStyling(currentTextInputElement.selectionStart, currentTextInputElement.selectionEnd, listType, 0);
      currentTextInstance.text.cursorPosition = currentTextInputElement.selectionStart;
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    function handleBoldShortcut () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.setPartialStyling(
        currentTextInputElement.selectionStart, currentTextInputElement.selectionEnd, 'font-weight',
        !currentBold
          ? 'bold'
          : 'normal'
      );

      currentBold = !currentBold;
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    function handleItalicShortcut () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.setPartialStyling(
        currentTextInputElement.selectionStart, currentTextInputElement.selectionEnd, 'font-style',
        !currentItalic
          ? 'italic'
          : 'normal'
      );

      currentItalic = !currentItalic;
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    function handleUnderlineShortcut () {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(currentTextInstance.dom, false);
      currentTextInstance.text.setPartialStyling(
        currentTextInputElement.selectionStart, currentTextInputElement.selectionEnd, 'text-decoration',
        !currentUnderline
          ? 'underline'
          : 'normal'
      );

      currentUnderline = !currentUnderline;
      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: currentTextInstance });
    }

    currentTextInstance.setCursorTextType(true);

    INKIVE.EditorFacade.on('zoom.changed', () => {
      updateSelection(null, true);
    });

    $(currentTextInputElement).unbind('paste').on('paste', function () {
      _.defer(function () {
        $(currentTextInputElement).trigger('keyup', { forceRedraw: true });
      });
    });

    $(currentTextInputElement).unbind('keydown').on('keydown', _.throttle(function (event) {
      event.which === tabKeyCode && event.preventDefault();
      currentCursorPosition = $currentTextInputElement.selectionStart;

      if (event.shiftKey && event.key === 'Tab') {
        handleShiftTab();
      } else if (event.key === 'Tab') {
        handleTab();
      } else if (event.which === backspaceCode && currentTextInputElement.selectionStart === currentTextInputElement.selectionEnd) {
        handleDeleteList();
      } else if (event[shortcutKey] && event.key === 'a') {
        event.preventDefault();
        selectAll();
      } else if (event[shortcutKey] && event.key === 'b') {
        event.preventDefault();
        handleBoldShortcut();
      } else if (event[shortcutKey] && event.key === 'i') {
        event.preventDefault();
        handleItalicShortcut();
      } else if (event[shortcutKey] && event.key === 'u') {
        event.preventDefault();
        handleUnderlineShortcut();
      }

      const moveToParentTextArea = currentCursorPosition === 0 &&
        ['Backspace', 'ArrowLeft'].includes(event.key) &&
        currentTextInstance.parent_text;

      moveToParentTextArea && jumpToPreviousTextArea();

      const moveToChildTextArea = currentCursorPosition === currentTextContent.length &&
        ['ArrowRight', 'Enter'].includes(event.key) &&
        currentTextInstance.child_text;

      moveToChildTextArea && setTimeout(() => jumpToNextTextArea(), 0);
    }, 0));

    $(currentTextInputElement).unbind('keyup').on('keyup', (event) => {
      onKeyUp(event);
    });
    const onTextInputObservable = fromEvent(currentTextInputElement, 'input') // Listen to input event will fix shortcut CMD-Z problem
      .pipe(
        throttleTime(0),
        tap(onInput));

    updateTextEventObserver = onTextInputObservable.pipe(debounceTime(250))
      .subscribe({
        next: () => {
          if (shouldTriggerTextUpdate) {
            INKIVE.DesignFacade.trigger('text.update');
            shouldTriggerTextUpdate = false;
          }
        },
        error: error => console.error(error),
        complete: () => console.info('complete')
      });

    saveTextEventObserver = onTextInputObservable.pipe(debounceTime(100))
      .subscribe({
        next: () => {
          if (shouldTriggerTextUpdate) {
            INKIVE.hasEditorUnsavedChanges = true;
          }
        },
        error: error => console.error(error),
        complete: () => console.info('complete')
      });
  }

  function clear (isTypewritter) {
    shouldTriggerTextUpdate = false;
    $('#element-popupmenu').removeClass('disabled');

    updateTextEventObserver?.unsubscribe?.();
    saveTextEventObserver?.unsubscribe?.();

    if (currentTextInstance) {
      currentTextInstance.setCursorTextType(false);
      const selectedElements = canvas.getSelectedElems();
      const isSelected = Array.isArray(selectedElements) && selectedElements.includes(currentTextInstance.dom);
      const isTableText = currentTextInstance.dom?.parentElement?.classList?.contains('table-cell');

      if (isTableText && isTypewritter) {
        canvas.getMode() === 'table-edit' && INKIVE.Table.endTableEditingMode();
        canvas.clearSelection();
      } else if (!isTableText && isSelected) {
        canvas.clearSelection();
        canvas.selectOnly([currentTextInstance.dom]);
      } else if (isTableText && isSelected) {
        canvas.selectOnly([currentTextInstance.dom.closest('.element.table')]);
      }

      if (selectionBlock) {
        selectionBlock.parentNode.removeChild(selectionBlock);
        selectionBlock = null;
      }

      if (textCursor) {
        $(textCursor).remove();
        textCursor = null;
      }
    }

    $(currentTextInputElement).blur();
    currentTextInstance && $(currentTextInstance.dom).off('updateCursorPosition');
    currentTextInstance = null;
    timesClickedOnElement = 0;
  }

  /** *************************** CURSOR POSITION *************************************/

  function jumpToPreviousTextArea (cursorPositionFromEnd = 0) {
    return jumpToLinkedTextArea(currentTextInstance.parent_text, () => {
      const blockEnd = currentTextInstance.text.getChars().length - 1;
      return blockEnd - cursorPositionFromEnd;
    });
  }

  function jumpToNextTextArea (cursorPositionFromStart = 0) {
    return jumpToLinkedTextArea(currentTextInstance.child_text,
      () => cursorPositionFromStart
    );
  }

  async function jumpToLinkedTextArea (linkId, getCursorPosition = () => 0) {
    const textAreaId = getLinkedTextAreaId(linkId);
    const elem = document.getElementById(textAreaId);

    await moveToLinkedTextAreaPage(linkId);

    INKIVE.EditorFacade.handle('select', textAreaId);
    canvas.selectorManager.requestSelector(elem).fadeSelectionGrips(false);

    initCache(elem);
    currentTextInputElement.focus();
    const cursorPosition = getCursorPosition();
    currentTextInputElement.setSelectionRange(cursorPosition, cursorPosition);
    updateSelection();
  }

  async function moveToLinkedTextAreaPage (linkId) {
    const nextBlockPage = getLinkedTextAreaPageId(linkId);

    if (nextBlockPage !== INKIVE.Tool._current_design_id) {
      // TODO: this should be instant. For tha to happen, Page Service needs to work as specified
      INKIVE.Tool.disableUI();
      currentTextInputElement.blur();
      canvas.setMode('select');

      await INKIVE.EditorFacade.handle('page', 'move_to', {
        save: true, // TODO: we should not be saving, we should be committing for handling save later
        design_id: nextBlockPage
      });

      INKIVE.Tool.enableUI();
    }
  }

  /**
   *     const evt       = opts.event,
   *      mouse_target = opts.mouse_target,
   *      mouse_x     = opts.mouse_x,
   *      mouse_y     = opts.mouse_y;
   */
  function updateCursorPosition (opts) {
    let target = opts.event ? opts.event.target : opts.elem;
    const mouseX = opts.mouse_x;
    const mouseY = opts.mouse_y;

    if (!currentTextInstance) return;

    isRtl = /[\u0591-\u07FF]/.test(currentTextInstance.text.textContent);

    if (target.tagName !== 'text' && target.tagName !== 'tspan') {
      target = target.tagName === 'a' && target.parentElement?.tagName === 'tspan'
        ? target.closest('text, tspan')
        : target.querySelector('text, tspan');
    }

    const { lineNumber, offsetFactor } = _getLineNumber(mouseX, mouseY);
    const offsetIndex = target ? 0 : offsetFactor;
    const index = Math.max(getIndexFromPoint(lineNumber, mouseX, mouseY, opts.isMoveCursor) - offsetIndex, 0);

    // Selection
    if (opts.isMoveCursor || opts.event.shiftKey) {
      if (index >= selectionInitialIndex) {
        currentTextInputElement.setSelectionRange(selectionInitialIndex, index);
      } else {
        currentTextInputElement.setSelectionRange(index, selectionInitialIndex);
      }
    } else {
      const start = opts.beginEditing ? 0 : index;
      const end = opts.beginEditing ? currentTextInstance.text.characterBBoxes.length : index;

      currentTextInputElement.setSelectionRange(start, end);

      if (!opts.beginEditing) {
        selectionInitialIndex = index;
      }
    }

    updateSelection();

    $(currentTextInstance.dom).closest('.sticker').length ||
    $(currentTextInstance.dom).trigger('updateCursorPosition');

    return index;
  }

  function setCursor (index, start, previousStart, chars) {
    const empty = (currentTextInputElement.value === '');
    $(currentTextInputElement).focus();

    if (!arguments.length) {
      if (empty) {
        index = 0;
      } else {
        index = pStart;
      }
    }

    chars = chars || currentTextInstance.text.getChars();
    let charbb = chars[index];

    if (start === chars.length && previousStart > start) {
      return jumpToNextTextArea(previousStart - start);
    }

    if (!charbb) {
      return null;
    }

    if (charbb.line) {
      const line = currentTextInstance.text.strings[charbb.line - 1];
      if (line && line.isBlank && charbb === '•') {
        charbb = chars[index + 1];
      }
    }

    // Get cursor element
    textCursor = svgedit.utilities.getElem('text_cursor');
    if (!textCursor) {
      textCursor = document.createElementNS(NS.SVG, 'line');
      const transform = getTextSelectorTransform(currentTextInstance);

      svgedit.utilities.assignAttributes(textCursor, {
        id: 'text_cursor',
        style: 'cursor: text; pointer-events: none',
        stroke: '#000000',
        'stroke-width': 2,
        transform
      });

      textCursorAnimation = document.createElementNS(NS.SVG, 'animate');
      svgedit.utilities.assignAttributes(textCursorAnimation, {
        id: 'text_cursor_animation',
        attributeType: 'CSS',
        attributeName: 'opacity',
        userSelect: 'none',
        from: '1',
        to: '0',
        repeatCount: 'indefinite',
        dur: '1s',
        begin: '0s',
        calcMode: 'discrete',
        fill: 'remove'
      });
      textCursor.appendChild(textCursorAnimation);

      textCursor = svgedit.utilities.getElem('selectorParentGroup').appendChild(textCursor);
    }

    charbb.dx = charbb.dx || 0;

    let charX = charbb.x - charbb.dx;
    if (isRtl) {
      const lineChars = _.where(chars, { line: charbb.line });
      const minChar = _.min(lineChars, function (char) {
        return char.x;
      });

      if (index === start && !charbb.char) {
        charX = minChar.x;
      } else {
        charX = charbb.x + charbb.width - charbb.dx;
      }
    }

    const startPt = _ptToScreen(charX, charbb.y);
    const endPt = _ptToScreen(charX, (charbb.y + charbb.height));

    svgedit.utilities.assignAttributes(textCursor, {
      x1: startPt.x,
      y1: startPt.y,
      x2: endPt.x,
      y2: endPt.y,
      visibility: 'visible',
      display: 'inline'
    });
  }

  function setCursorByBBox (bbox) {
    // Get cursor element
    textCursor = svgedit.utilities.getElem('text_cursor');
    if (!textCursor) {
      const selectorGroup = svgedit.utilities.getElem('selectorGroup0');
      const transform = selectorGroup.getAttribute('transform');

      textCursor = document.createElementNS(NS.SVG, 'line');
      svgedit.utilities.assignAttributes(textCursor, {
        id: 'text_cursor',
        style: 'cursor: text',
        stroke: '#000000',
        'stroke-width': 2,
        transform
      });

      textCursorAnimation = document.createElementNS(NS.SVG, 'animate');
      svgedit.utilities.assignAttributes(textCursorAnimation, {
        id: 'text_cursor_animation',
        attributeType: 'CSS',
        attributeName: 'opacity',
        from: '1',
        to: '0',
        repeatCount: 'indefinite',
        dur: '1s',
        begin: '0s',
        calcMode: 'discrete',
        fill: 'remove'
      });
      textCursor.appendChild(textCursorAnimation);

      textCursor = svgedit.utilities.getElem('selectorParentGroup').appendChild(textCursor);
    }

    const charX = bbox.x + bbox.width;
    const startPt = _ptToScreen(charX, bbox.y);
    const endPt = _ptToScreen(charX, (bbox.y + bbox.height));

    svgedit.utilities.assignAttributes(textCursor, {
      x1: startPt.x,
      y1: startPt.y,
      x2: endPt.x,
      y2: endPt.y,
      visibility: 'visible',
      display: 'inline'
    });
  }

  function getIndexFromPoint (lineNumber, mouseX, mouseY, isMoveCursor) {
    if (lineNumber < 0) {
      return 0;
    }

    const out = _screenToPt(mouseX, mouseY);
    let index = 0;
    for (let i = 0; i < lineNumber; i++) {
      index += currentTextInstance.text.strings[i].getChars().length;
    }

    const characterBBoxes = currentTextInstance.text.strings[lineNumber].getChars();
    const lastIndex = characterBBoxes.length - 1;

    const modifier = isRtl ? -1 : 1;
    const endOfLinePosition = characterBBoxes[lastIndex].x + characterBBoxes[lastIndex].width * modifier;
    const clickedOnEOL = isRtl ? out.x <= endOfLinePosition : out.x >= endOfLinePosition;
    if (clickedOnEOL) {
      return index + characterBBoxes.length;
    }

    if (isRtl) {
      let rtlIndex = index;

      for (let j = 0; j < characterBBoxes.length; j++) {
        const charX = characterBBoxes[j].x + characterBBoxes[j].width / 2;

        if (out.x < charX) {
          rtlIndex++;
        } else {
          break;
        }
      }
      index = rtlIndex;
    } else {
      for (let j = 0; j < characterBBoxes.length; j++) {
        if (out.x > characterBBoxes[j].x + characterBBoxes[j].width / 2) {
          index++;
        } else {
          break;
        }
      }
    }

    return index;
  }

  function getCursorLineIndex (lineArray, currentCursorPosition, textAnchor = 'start') {
    let cursorLineIndex, lineIndex;
    let total = 0;
    for (lineIndex = 0; lineIndex < lineArray.length; lineIndex++) {
      total += lineArray[lineIndex];
      if (total > currentCursorPosition) {
        cursorLineIndex = currentCursorPosition - (total - lineArray[lineIndex]);
        break;
      }
    }

    cursorLineIndex = handleRightAlignment(lineArray, lineIndex, cursorLineIndex, textAnchor);
    return [lineIndex, cursorLineIndex];
  }

  function getCursorTextIndex (lineArray, lineIndex, cursorLineIndex, textAnchor = 'start') {
    let cursorTextIndex = 0;
    for (let i = 0; i < lineIndex; i++) {
      cursorTextIndex += lineArray[i];
    }
    cursorLineIndex = handleRightAlignment(lineArray, lineIndex, cursorLineIndex, textAnchor);
    cursorTextIndex += cursorLineIndex;
    return cursorTextIndex;
  }

  function getLineArray (editingText, numOfLines) {
    const lineArray = [];
    // Store the length of each line in an array
    for (let i = 0; i < numOfLines; i++) {
      const line = editingText.querySelectorAll('text')[i];
      const isLineWithEol = line.classList.contains('eol');
      const isBlankLine = line.classList.contains('blank');
      const orderedLine = line.querySelector('tspan.ordered');
      const bulletsLine = line.querySelector('tspan.bullets');

      let handleListLine = 0;
      if (orderedLine) handleListLine += orderedLine.textContent.length;
      if (bulletsLine) handleListLine += bulletsLine.textContent.length;

      // Number of slots for cursor depends on the line is `eol` or `blank`
      const textLength = isLineWithEol && !isBlankLine
        ? line.textContent.length
        : line.textContent.length - 1;

      // Not counting the bullet or ordered characters
      lineArray.push(textLength + 1 - handleListLine);
    }
    return lineArray;
  }

  function handleRightAlignment (lineArray, lineIndex, cursorLineIndex, textAnchor) {
    // If the text is right aligned, the cursor index counts from the end
    if (textAnchor === 'end') {
      cursorLineIndex = (lineArray[lineIndex] - 1) - cursorLineIndex;
    }
    return cursorLineIndex;
  }

  /** *************************** SELECTION *************************************/

  function selectWord (opts, shouldHideSelectionBlock) {
    const evt = opts.event;
    const mouseX = opts.mouse_x;
    const mouseY = opts.mouse_y;

    if (!currentTextInstance) return;

    if (!evt || !$(evt.target).is('tspan, text')) return;

    const { lineNumber: inx } = _getLineNumber(mouseX, mouseY);
    const index = getIndexFromPoint(inx, mouseX, mouseY);

    const chars = currentTextInstance.text.getChars();

    let first = -1;
    let last = -1;
    const isNonWordChar = function (char) {
      return !char || (char.match(/\W+/i) && !char.match(/[À-ÿ]/) && !char.match(/[\u0591-\u07FF]/));
    };

    for (let i = index; i < chars.length; i++) {
      if (isNonWordChar(chars[i].char)) {
        last = i;
        break;
      }
    }

    if (chars.length) {
      for (let j = Math.min(index, chars.length - 1); j >= 0; j--) {
        if (isNonWordChar(chars[j].char)) {
          first = j + 1;
          break;
        }
      }
    }

    if (first === -1) first = 0;
    if (last === -1) last = chars.length - 1;

    currentTextInputElement.setSelectionRange(first, shouldHideSelectionBlock ? first : last);
    updateSelection();
  }

  function selectAll () {
    if (!currentTextInstance) return;
    const index = currentTextInstance.text.getChars().length - 1;
    currentTextInputElement.setSelectionRange(0, index);
    updateSelection();
  }

  function getLineIndexOfCursor (cursorPosition) {
    const editingText = currentTextInstance.dom;
    const numOfLines = editingText.querySelectorAll('text').length;
    if (!numOfLines) return 0;

    const lineArray = getLineArray(editingText, numOfLines);
    const [lineIndex] = getCursorLineIndex(lineArray, cursorPosition);
    return lineIndex;
  }

  function triggerEventCursorPositionChanged (cursorPosition) {
    const lineIndex = getLineIndexOfCursor(cursorPosition);
    const paragraphIndex = INKIVE.Text.Helpers.getParagraphIndex(currentTextInstance.text, lineIndex);
    const defaultStringInstance = {
      textAnchor: 'start',
      bulletLevel: 0,
      bulletStyle: 0,
      bulletFill: '#000000',
      bulletWidth: 0,
      orderedLevel: 0,
      orderedStyle: 0,
      orderedNumberingStyle: 0,
      orderedRegex: '',
      orderedFill: '#000000',
      orderedStart: null,
      orderedWidth: 0
    };
    const currentStringInstance = currentTextInstance.text.strings[lineIndex] || defaultStringInstance;
    const tablePadding = tableQuery.getCellPadding(currentTextInstance.dom.parentElement);

    cursorPosition = currentTextInstance.text.fakeCharIndexToReal(cursorPosition);

    let paragraphSpacing = INKIVE.Text.Helpers.getParagraphSpacing(currentTextInstance.text.dom);
    let lineSpacing = INKIVE.Text.Helpers.getLineSpacing(currentTextInstance.text.dom);
    let lineMargin = INKIVE.Text.Helpers.getLineMargin(currentTextInstance.text.dom);
    let firstLineMargin = INKIVE.Text.Helpers.getFirstLineMargin(currentTextInstance.text.dom);
    let rightMargin = INKIVE.Text.Helpers.getRightMargin(currentTextInstance.text.dom);
    let letterSpacing = currentTextInstance.text.letter_spacing;

    if (Array.isArray(lineMargin)) lineMargin = lineMargin[paragraphIndex];
    if (Array.isArray(firstLineMargin)) firstLineMargin = firstLineMargin[paragraphIndex];
    if (Array.isArray(rightMargin)) rightMargin = rightMargin[paragraphIndex];
    if (Array.isArray(lineSpacing)) lineSpacing = lineSpacing[paragraphIndex];
    if (Array.isArray(paragraphSpacing)) paragraphSpacing = paragraphSpacing[paragraphIndex];
    if (Array.isArray(letterSpacing)) letterSpacing = letterSpacing[cursorPosition];

    const selectionStyleData = {
      fill: currentTextInstance.text.fill || '#000000',
      highlight: currentTextInstance.text.highlight, // Can be null
      fontFamily: currentTextInstance.text.font_family || 'Lato',
      fontSize: currentTextInstance.text.font_size || '50',
      isBold: currentTextInstance.text.font_weight === 'bold' || currentTextInstance.text.font_weight >= 700,
      fontStretch: currentTextInstance.text.font_stretch || 'normal',
      isItalic: currentTextInstance.text.font_style === 'italic' || currentTextInstance.text.font_style === 'oblique',
      isUnderlined: currentTextInstance.text.text_decoration === 'underline',
      link: currentTextInstance.text.link || '',
      textAnchor: currentStringInstance.textAnchor || currentTextInstance.text.text_anchor || 'start',
      heading: Number(currentStringInstance.heading) || Number(currentTextInstance.heading) || 'Text Block',
      bullets: !!currentStringInstance.bulletLevel,
      ordered: !!currentStringInstance.orderedLevel,
      orderedStyle: currentStringInstance.orderedStyle || defaultStringInstance.orderedStyle,
      orderedNumberingStyle: currentStringInstance.orderedNumberingStyle || defaultStringInstance.orderedNumberingStyle,
      orderedRegex: currentStringInstance.orderedRegex || defaultStringInstance.orderedRegex,
      bulletStyle: currentStringInstance.bulletStyle || defaultStringInstance.bulletStyle,
      bulletFill: currentStringInstance.bulletFill || currentTextInstance.text.fill || defaultStringInstance.bulletFill,
      bulletWidth: currentStringInstance.bulletWidth || defaultStringInstance.bulletWidth,
      orderedWidth: currentStringInstance.orderedWidth || defaultStringInstance.orderedWidth,
      orderedFill: currentStringInstance.orderedFill || currentTextInstance.text.fill || defaultStringInstance.orderedFill,
      orderedStart: currentStringInstance.orderedStart || defaultStringInstance.orderedStart,
      lineMargin: lineMargin - tablePadding.left || 0,
      firstLineMargin: Math.max(firstLineMargin - tablePadding.left, 0) || 0,
      rightMargin: rightMargin - tablePadding.right || 0,
      lineSpacing: lineSpacing || 0,
      paragraphSpacing: paragraphSpacing || 0,
      letterSpacing: convertPixelToRem(letterSpacing, currentTextInstance.text.font_size).toFixed(0) || 0,
      customPlaceholder: currentTextInstance.text.custom_placeholder || '',
      dataDefinedPlaceHolder: currentTextInstance.text.data_defined_placeholder || '',
      meta: currentTextInstance.data_meta || undefined,
      processed: false
    };

    const styles = currentTextInstance.text.styles;
    for (let i = 0; i < styles.length; i++) {
      const style = styles[i];
      const isInRange = cursorPosition >= style.start && cursorPosition <= style.end;
      if (!isInRange) continue;

      switch (style.attribute) {
        case 'fill':
          selectionStyleData.fill = style.value;
          break;
        case 'highlight':
          selectionStyleData.highlight = style.value;
          break;
        case 'font-family':
          selectionStyleData.fontFamily = style.value;
          break;
        case 'font-size':
          selectionStyleData.fontSize = style.value;
          break;
        case 'font-weight':
          selectionStyleData.isBold = style.value === 'bold' || style.value >= 700;
          break;
        case 'font-stretch':
          selectionStyleData.fontStretch = style.value;
          break;
        case 'font-style':
          selectionStyleData.isItalic = style.value === 'italic';
          break;
        case 'text-decoration':
          selectionStyleData.isUnderlined = style.value === 'underline';
          break;
        case 'link':
          selectionStyleData.link = style.value;
          break;
        case 'data-meta':
          selectionStyleData.meta = style.value;
          break;
        case 'custom-placeholder':
          selectionStyleData.customPlaceholder = style.value;
          break;
        case 'data-defined-placeholder':
          selectionStyleData.dataDefinedPlaceHolder = style.value;
          break;
      }
    }

    INKIVE.EditorFacade.trigger('text.selection_changed', selectionStyleData);
    INKIVE.EditorFacade.trigger('link.selection_changed', selectionStyleData);
    selectionStyleData.bulletStyle && INKIVE.EditorFacade.trigger('bullet.selection_changed', selectionStyleData);
    selectionStyleData.orderedStyle && INKIVE.EditorFacade.trigger('ordered.selection_changed', selectionStyleData);

    currentBold = selectionStyleData.isBold;
    currentItalic = selectionStyleData.isItalic;
    currentUnderline = selectionStyleData.isUnderlined;
    INKIVE.EditorFacade.trigger('placeholder.selection_changed', selectionStyleData);
  }

  function triggerEventSelectionRangeChanged (start, end) {
    const lineIndex = getLineIndexOfCursor(start);
    const paragraphIndex = INKIVE.Text.Helpers.getParagraphIndex(currentTextInstance.text, lineIndex);
    const currentStringInstance = currentTextInstance.text.strings[lineIndex];
    const tablePadding = tableQuery.getCellPadding(currentTextInstance.dom.parentElement);

    start = currentTextInstance.text.fakeCharIndexToReal(start);
    end = currentTextInstance.text.fakeCharIndexToReal(end);

    let paragraphSpacing = INKIVE.Text.Helpers.getParagraphSpacing(currentTextInstance.text.dom);
    let lineSpacing = INKIVE.Text.Helpers.getLineSpacing(currentTextInstance.text.dom);
    let lineMargin = INKIVE.Text.Helpers.getLineMargin(currentTextInstance.text.dom);
    let firstLineMargin = INKIVE.Text.Helpers.getFirstLineMargin(currentTextInstance.text.dom);
    let rightMargin = INKIVE.Text.Helpers.getRightMargin(currentTextInstance.text.dom);
    let letterSpacing = currentTextInstance.text.letter_spacing;

    if (Array.isArray(lineMargin)) lineMargin = lineMargin[paragraphIndex];
    if (Array.isArray(firstLineMargin)) firstLineMargin = firstLineMargin[paragraphIndex];
    if (Array.isArray(rightMargin)) rightMargin = rightMargin[paragraphIndex];
    if (Array.isArray(lineSpacing)) lineSpacing = lineSpacing[paragraphIndex];
    if (Array.isArray(paragraphSpacing)) paragraphSpacing = paragraphSpacing[paragraphIndex];
    if (Array.isArray(letterSpacing)) letterSpacing = letterSpacing[start];

    // Objects to hold styles for the selected range
    const fill = {};
    const highlight = {};
    const fontSize = {};
    const fontFamily = {};
    const isBold = {};
    const isItalic = {};
    const isUnderlined = {};
    const link = {};
    const customPlaceholder = {};
    const dataDefinedPlaceHolder = {};
    const meta = {};
    const fontStretch = {};

    // Define valid indices for style objects (with default global values)
    for (let i = start; i < end; i++) {
      fill[i] = currentTextInstance.text.fill || '#000000';
      highlight[i] = currentTextInstance.text.highlight; // Can be null (still valid)
      fontFamily[i] = currentTextInstance.text.font_family || 'Lato';
      fontSize[i] = currentTextInstance.text.font_size || '50';
      isBold[i] = currentTextInstance.text.font_weight === 'bold' || currentTextInstance.text.font_weight >= 700;
      fontStretch[i] = currentTextInstance.text.font_stretch || 'normal';
      isItalic[i] = currentTextInstance.text.font_style === 'italic' || currentTextInstance.text.font_style === 'oblique';
      isUnderlined[i] = currentTextInstance.text.text_decoration === 'underline';
      link[i] = currentTextInstance.text.link || '';
      meta[i] = currentTextInstance.data_meta || undefined;
      customPlaceholder[i] = currentTextInstance.text.custom_placeholder;
      dataDefinedPlaceHolder[i] = currentTextInstance.text.data_defined_placeholder;
    }

    const styles = currentTextInstance.text.styles;
    for (let i = 0; i < styles.length; i++) {
      const style = styles[i];
      // For each step of style, test if it is a valid key then use its value
      for (let j = style.start; j < style.end; j++) {
        if (j < start || j >= end) continue;

        switch (style.attribute) {
          case 'fill':
            fill[j] = style.value;
            break;
          case 'highlight':
            highlight[j] = style.value;
            break;
          case 'font-family':
            fontFamily[j] = style.value;
            break;
          case 'font-size':
            fontSize[j] = style.value;
            break;
          case 'font-weight':
            isBold[j] = style.value === 'bold' || style.value >= 700;
            break;
          case 'font-stretch':
            fontStretch[j] = style.value;
            break;
          case 'font-style':
            isItalic[j] = style.value === 'italic';
            break;
          case 'text-decoration':
            isUnderlined[j] = style.value === 'underline';
            break;
          case 'link':
            link[j] = style.value;
            break;
          case 'data-meta':
            meta[j] = style.value;
            break;
          case 'custom-placeholder':
            customPlaceholder[j] = style.value;
            break;
          case 'data-defined-placeholder':
            dataDefinedPlaceHolder[j] = style.value;
            break;
        }
      }
    }

    // Get value at the selection start point (possibly updated with the previous click)
    const fillValues = Object.values(fill);
    const startFillColor = fillValues[0];

    const highlightValues = Object.values(highlight);
    const startHighlightColor = highlightValues[0];

    const fontFamilyValues = Object.values(fontFamily);
    const startFontFamily = fontFamilyValues[0];

    const fontSizeValues = Object.values(fontSize);
    const startFontSize = fontSizeValues[0];

    const isBoldValues = Object.values(isBold);
    const startIsBold = isBoldValues[0];

    const fontStretchValues = Object.values(fontStretch);
    const startFontStretch = fontStretchValues[0];

    const isItalicValues = Object.values(isItalic);
    const startIsItalic = isItalicValues[0];

    const isUnderlinedValues = Object.values(isUnderlined);
    const startIsUnderlined = isUnderlinedValues[0];

    const linkValues = Object.values(link);
    const startLink = linkValues[0];

    const metaValues = Object.values(meta);
    const startMeta = metaValues[0];

    const customPlaceholderValues = Object.values(customPlaceholder);
    const startCustomPlaceholder = customPlaceholderValues[0];

    const dataDefinedPlaceHolderValues = Object.values(dataDefinedPlaceHolder);
    const startDataDefinedPlaceHolder = dataDefinedPlaceHolderValues[0];

    // Does all the range use the same value as the value at the start point?
    const consistentFillColor = fillValues.every(v => v === startFillColor);
    const consistentHighlightColor = highlightValues.every(v => v === startHighlightColor);
    const consistentFontFamily = fontFamilyValues.every(v => v === startFontFamily);
    const consistentFontSize = fontSizeValues.every(v => v === startFontSize);
    const consistentIsBold = isBoldValues.every(v => v === startIsBold);
    const consistentIsItalic = isItalicValues.every(v => v === startIsItalic);
    const consistentIsUnderlined = isUnderlinedValues.every(v => v === startIsUnderlined);
    const consistentLink = linkValues.every(v => v === startLink);
    const consistentMeta = metaValues.every(v => v === startMeta);
    const consistentCustomPlaceholder = customPlaceholderValues.every(v => v === startCustomPlaceholder);
    const consistentDataDefinedPlaceHolder = dataDefinedPlaceHolderValues.every(v => v === startDataDefinedPlaceHolder);
    const consistentFontStretch = fontStretchValues.every(v => v === startFontStretch);

    const rangeSelectionStyleData = { processed: false };
    rangeSelectionStyleData.fill = consistentFillColor ? startFillColor : '#000000';
    rangeSelectionStyleData.highlight = consistentHighlightColor ? startHighlightColor : null;
    rangeSelectionStyleData.fontFamily = consistentFontFamily ? startFontFamily : 'Mixed';
    rangeSelectionStyleData.fontSize = consistentFontSize ? startFontSize : '?';
    rangeSelectionStyleData.isBold = consistentIsBold ? startIsBold : false;
    rangeSelectionStyleData.fontStretch = consistentFontStretch ? startFontStretch : 'normal';
    rangeSelectionStyleData.isItalic = consistentIsItalic ? startIsItalic : false;
    rangeSelectionStyleData.isUnderlined = consistentIsUnderlined ? startIsUnderlined : false;
    rangeSelectionStyleData.link = consistentLink ? startLink : '';
    rangeSelectionStyleData.meta = consistentMeta ? startMeta : undefined;
    rangeSelectionStyleData.customPlaceholder = consistentCustomPlaceholder ? startCustomPlaceholder : '';
    rangeSelectionStyleData.dataDefinedPlaceHolder = consistentDataDefinedPlaceHolder ? startDataDefinedPlaceHolder : '';
    rangeSelectionStyleData.lineMargin = lineMargin - tablePadding.left || 0;
    rangeSelectionStyleData.firstLineMargin = Math.max(firstLineMargin - tablePadding.left, 0) || 0;
    rangeSelectionStyleData.rightMargin = rightMargin - tablePadding.right || 0;
    rangeSelectionStyleData.lineSpacing = lineSpacing || 0;
    rangeSelectionStyleData.paragraphSpacing = paragraphSpacing || 0;
    rangeSelectionStyleData.letterSpacing = convertPixelToRem(letterSpacing, currentTextInstance.text.font_size).toFixed(0) || 0;
    rangeSelectionStyleData.ordered = !!currentStringInstance.orderedLevel;
    rangeSelectionStyleData.orderedStyle = currentStringInstance.orderedStyle || 0;
    rangeSelectionStyleData.orderedNumberingStyle = currentStringInstance.orderedNumberingStyle || 0;
    rangeSelectionStyleData.orderedRegex = currentStringInstance.orderedRegex || '';
    rangeSelectionStyleData.orderedWidth = currentStringInstance.orderedWidth || 0;
    rangeSelectionStyleData.orderedFill = currentStringInstance.orderedFill || rangeSelectionStyleData.fill;
    rangeSelectionStyleData.orderedStart = currentStringInstance.orderedStart || null;
    rangeSelectionStyleData.bullets = !!currentStringInstance.bulletLevel;
    rangeSelectionStyleData.bulletStyle = currentStringInstance.bulletStyle || 0;
    rangeSelectionStyleData.bulletWidth = currentStringInstance.bulletWidth || 0;
    rangeSelectionStyleData.bulletFill = currentStringInstance.bulletFill || rangeSelectionStyleData.fill;
    rangeSelectionStyleData.textAnchor = currentStringInstance.textAnchor
      ? currentStringInstance.textAnchor
      : currentTextInstance.text.text_anchor;
    rangeSelectionStyleData.heading = Number(currentStringInstance.heading) || Number(currentTextInstance.heading) || 'Text Block';

    INKIVE.EditorFacade.trigger('text.selection_changed', rangeSelectionStyleData);
    rangeSelectionStyleData.bulletStyle && INKIVE.EditorFacade.trigger('bullet.selection_changed', rangeSelectionStyleData);
    rangeSelectionStyleData.orderedStyle && INKIVE.EditorFacade.trigger('ordered.selection_changed', rangeSelectionStyleData);
    INKIVE.EditorFacade.trigger('link.selection_changed', rangeSelectionStyleData);

    currentBold = rangeSelectionStyleData.isBold;
    currentItalic = rangeSelectionStyleData.isItalic;
    currentUnderline = rangeSelectionStyleData.isUnderlined;
    INKIVE.EditorFacade.trigger('placeholder.selection_changed', rangeSelectionStyleData);
  }

  function updateSelection (previousStart, typing = false) {
    if (canvas.getMode() !== 'textedit') {
      return;
    }

    const i1 = currentTextInputElement.selectionStart;
    const i2 = currentTextInputElement.selectionEnd;

    const start = Math.min(i1, i2);
    const end = Math.max(i1, i2);

    currentTextInstance.setCursorTextType(true);
    const characterBBoxes = currentTextInstance.text.characterBBoxes || currentTextInstance.text.getChars();
    setCursor(end, start, previousStart, characterBBoxes);
    setSelection(start, end, characterBBoxes);

    // Trigger an event to update text editing toolbar
    if (typing) return;
    end === start ? triggerEventCursorPositionChanged(start) : triggerEventSelectionRangeChanged(start, end);
  }

  function setSelection (start, end, characterBBoxes) {
    characterBBoxes = characterBBoxes || currentTextInstance.text.getChars();

    function block (dstr) {
      const block = document.createElementNS(NS.SVG, 'path');

      svgedit.utilities.assignAttributes(block, {
        fill: 'blue',
        opacity: 0.5,
        style: 'pointer-events:none'
      });

      svgedit.utilities.assignAttributes(block, {
        d: dstr,
        display: 'inline'
      });

      selectionBlock.appendChild(block);
    }

    selectionBlock = svgedit.utilities.getElem('text_selectblock');

    if (selectionBlock) {
      svgedit.utilities.getElem('selectorParentGroup').removeChild(selectionBlock);
    }

    const transform = getTextSelectorTransform(currentTextInstance);

    selectionBlock = document.createElementNS(NS.SVG, 'g');

    svgedit.utilities.assignAttributes(selectionBlock, {
      id: 'text_selectblock',
      style: 'pointer-events:none',
      transform
    });

    svgedit.utilities.getElem('selectorParentGroup').appendChild(selectionBlock);

    let startbb = characterBBoxes[start];
    const endbb = characterBBoxes[end];

    if (!startbb || !endbb) {
      return null;
    }

    let point = start;

    if (isRtl) {
      for (let i = start; i <= end; i++) {
        if (startbb.y !== characterBBoxes[i].y) {
          const tr = _ptToScreen(startbb.x + startbb.width, getYPosition(startbb));
          const tl = _ptToScreen(characterBBoxes[i - 1].x + characterBBoxes[i - 1].width, getYPosition(startbb));
          const br = _ptToScreen(startbb.x + startbb.width, getYPosition(startbb) + getHeight(startbb));
          const bl = _ptToScreen(characterBBoxes[i - 1].x + characterBBoxes[i - 1].width,
            getYPosition(characterBBoxes[i - 1]) + getHeight(startbb));
          const dstr = 'M' + tl.x + ',' + tl.y +
            ' L' + tr.x + ',' + tr.y +
            ' ' + br.x + ',' + br.y +
            ' ' + bl.x + ',' + bl.y + 'z';
          block(dstr);
          startbb = characterBBoxes[i];
          point = i;
        }
      }
      if (point !== end) {
        const endWidth = endbb.x + endbb.width;

        const tr = _ptToScreen(startbb.x + startbb.width, getYPosition(startbb));
        const tl = _ptToScreen(endWidth, getYPosition(startbb));
        const br = _ptToScreen(startbb.x + startbb.width, getYPosition(startbb) + getHeight(startbb));
        const bl = _ptToScreen(endWidth, getYPosition(endbb) + getHeight(startbb));

        const dstr = 'M' + tl.x + ',' + tl.y +
          ' L' + tr.x + ',' + tr.y +
          ' ' + br.x + ',' + br.y +
          ' ' + bl.x + ',' + bl.y + 'z';
        block(dstr);
      }
    } else {
      for (let i = start; i <= end; i++) {
        if (startbb.y !== characterBBoxes[i].y) {
          const tl = _ptToScreen(startbb.x, getYPosition(startbb));
          const tr = _ptToScreen(characterBBoxes[i - 1].x + characterBBoxes[i - 1].width, getYPosition(startbb));
          const bl = _ptToScreen(startbb.x, getYPosition(startbb) + getHeight(startbb));
          const br = _ptToScreen(characterBBoxes[i - 1].x + characterBBoxes[i - 1].width,
            getYPosition(characterBBoxes[i - 1]) + getHeight(startbb));

          const dstr = 'M' + tl.x + ',' + tl.y +
            ' L' + tr.x + ',' + tr.y +
            ' ' + br.x + ',' + br.y +
            ' ' + bl.x + ',' + bl.y + 'z';
          block(dstr);
          startbb = characterBBoxes[i];
          point = i;
        }
      }
      if (point !== end) {
        const tl = _ptToScreen(startbb.x, getYPosition(startbb));
        const tr = _ptToScreen(endbb.x, getYPosition(startbb));
        const bl = _ptToScreen(startbb.x, getYPosition(startbb) + getHeight(startbb));
        const br = _ptToScreen(endbb.x, getYPosition(endbb) + getHeight(startbb));

        const dstr = 'M' + tl.x + ',' + tl.y +
          ' L' + tr.x + ',' + tr.y +
          ' ' + br.x + ',' + br.y +
          ' ' + bl.x + ',' + bl.y + 'z';
        block(dstr);
      }
    }

    function getHeight (bbox) {
      return bbox.fontSize || parseFloat(currentTextInstance.text.font_size);
    }

    function getYPosition (bbox) {
      return bbox.y + (bbox.height - getHeight(bbox)) / 2;
    }
  }

  function setSectionByBBox (bbox) {
    selectionBlock = svgedit.utilities.getElem('text_selectblock');

    if (selectionBlock) {
      svgedit.utilities.getElem('selectorParentGroup').removeChild(selectionBlock);
    }
    const selectorGroup = svgedit.utilities.getElem('selectorGroup0');
    const transform = selectorGroup.getAttribute('transform');

    selectionBlock = document.createElementNS(NS.SVG, 'g');

    svgedit.utilities.assignAttributes(selectionBlock, {
      id: 'text_selectblock',
      style: 'pointer-events:none',
      transform
    });

    svgedit.utilities.getElem('selectorParentGroup').appendChild(selectionBlock);

    const tl = _ptToScreen(bbox.x, bbox.y);
    const tr = _ptToScreen(bbox.x + bbox.width, bbox.y);
    const bl = _ptToScreen(bbox.x, bbox.y + bbox.height);
    const br = _ptToScreen(bbox.x + bbox.width, bbox.y + bbox.height);

    const dstr = 'M' + tl.x + ',' + tl.y +
          ' L' + tr.x + ',' + tr.y +
          ' ' + br.x + ',' + br.y +
          ' ' + bl.x + ',' + bl.y + 'z';
    const block = document.createElementNS(NS.SVG, 'path');

    svgedit.utilities.assignAttributes(block, {
      fill: 'blue',
      opacity: 0.5,
      style: 'pointer-events:none'
    });

    svgedit.utilities.assignAttributes(block, {
      d: dstr,
      display: 'inline'
    });

    selectionBlock.appendChild(block);
  }

  /** *************************** PRIVATE METHODS *************************************/
  function _calculateDistance (x1, y1, x2, y2) {
    return Math.hypot(x2 - x1, y2 - y1);
  }

  function _calculateOffsetFactor (shouldCalculateFromLeft, isRightToLeft) {
    if (shouldCalculateFromLeft && isRightToLeft) {
      return 1;
    } else if (shouldCalculateFromLeft && !isRightToLeft) {
      return 0;
    } else if (!shouldCalculateFromLeft && isRightToLeft) {
      return 0;
    } else if (!shouldCalculateFromLeft && !isRightToLeft) {
      return 1;
    }
    return 0;
  }

  function _getLineNumber (mouseX, mouseY) {
    let lineNumber = -1;
    let shouldCalculateFromLeft = false;
    const out = _screenToPt(mouseX, mouseY);
    const strings = currentTextInstance.text.strings;
    const isRightToLeft = currentTextInstance.text.direction === 'rtl';

    const lines = [];
    for (let i = strings.length - 1; i >= 0; i--) {
      const characterBBoxes = strings[i].getChars();
      if (!characterBBoxes || !characterBBoxes.length) continue;

      const startIndex = isRightToLeft ? characterBBoxes.length - 1 : 0;
      const endIndex = isRightToLeft ? 0 : characterBBoxes.length - 1;
      if (characterBBoxes[startIndex].y > out.y) continue;
      const line = {
        lineIndex: i,
        startX: characterBBoxes[startIndex].x,
        endX: characterBBoxes[endIndex].x,
        y: characterBBoxes[startIndex].y
      };
      lines.push(line);
    }

    let distance = Infinity;
    const lastLinesOfColumn = [];
    for (let j = 0; j < lines.length; j++) {
      const line = lines[j];
      if (j === 0) {
        lastLinesOfColumn.push(line);
      } else {
        const isConsecutive = line.lineIndex === lines[j - 1].lineIndex - 1 && line.y < lines[j - 1].y;
        isConsecutive || lastLinesOfColumn.push(line);
      }
    }

    for (const line of lastLinesOfColumn) {
      const isMouseInLeft = Math.abs(out.x - line.startX) < Math.abs(out.x - line.endX);
      const closestLineX = isMouseInLeft ? line.startX : line.endX;
      const tempDistance = _calculateDistance(out.x, out.y, closestLineX, line.y);
      if (tempDistance < distance) {
        distance = tempDistance;
        lineNumber = line.lineIndex;
        shouldCalculateFromLeft = isMouseInLeft;
      }
    }

    const offsetFactor = _calculateOffsetFactor(shouldCalculateFromLeft, isRightToLeft);

    return { lineNumber, offsetFactor };
  }

  function _screenToPt (xIn, yIn) {
    const out = {
      x: xIn,
      y: yIn
    };

    const zoom = canvas.getZoom();
    out.x /= zoom;
    out.y /= zoom;

    if (matrix) $.extend(out, svgedit.math.transformPoint(out.x, out.y, matrix.inverse()));

    return out;
  }

  function getTextSelectorTransform (currentTextInstance) {
    const currentTextElement = currentTextInstance.dom.closest('.element');
    const table = currentTextElement.closest('.element.table');
    const currentElement = table || currentTextElement;
    const centerElementBBox = currentElement.getBBox();
    const centerX = centerElementBBox.x + centerElementBBox.width / 2;
    const centerY = centerElementBBox.y + centerElementBBox.height / 2;
    const centerPoint = _ptToScreen(centerX, centerY);

    const selectorGroup = svgedit.utilities.getElem('selectorGroup0');
    const transform = selectorGroup.getAttribute('transform') ?? undefined;

    if (!transform) {
      return transform;
    }

    if (transform.startsWith('rotate')) {
      const rotateArgumentsString = transform.replace('rotate(', '').replace(')', '');
      const rotateArguments = rotateArgumentsString.split(',');
      rotateArguments[1] = centerPoint.x.toString();
      rotateArguments[2] = centerPoint.y.toString();
      return `rotate(${rotateArguments.join(',')})`;
    } else {
      return transform;
    }
  }

  function _ptToScreen (xIn, yIn) {
    const out = {
      x: xIn,
      y: yIn
    };

    // if (matrix) $.extend(out, svgedit.math.transformPoint(out.x, out.y, matrix));

    const zoom = canvas.getZoom();
    out.x *= zoom;
    out.y *= zoom;

    return out;
  }

  function startTextEditing (opts) {
    // Don't allow editing text if we are editing a table
    if (canvas.getMode() === 'table-edit' && !opts.isTypewritter) {
      INKIVE.Table.endTableEditingMode();
    }

    const hasNativeSupport = INKIVE.C.IS_MOBILE_APP && (
      AppBridge.version.isApp('webrand') ||
      AppBridge.version.isApp('pdf') ||
      (!window.Android && !AppBridge.version.isLowerThan('1.2')) ||
      (window.Android && !AppBridge.version.isLowerThan('1.2'))
    );

    if (hasNativeSupport) return;

    const isSubElement = opts.elem?.classList?.contains('subelement');
    const isTableText = opts.elem?.parentElement?.classList?.contains('table-cell');

    if (!isSubElement) {
      if (isTableText) {
        canvas.clearSelection();
        canvas.addToSelection([opts.elem], false);
      } else {
        svgedit.select.getSelectorManager().requestSelector(opts.elem).showGrips(false);
      }
    }

    if (opts.isTypewritter) {
      $('#element-popupmenu').addClass('disabled');
    } else {
      canvas.setMode('textedit');
      INKIVE.Menus.elementPopupMenu.refresh();
    }

    initCache(opts.elem);
    return true;
  }

  function ifEditText (opts, beginTextTyping, beginTextEditing) {
    let mouseTarget = opts.mouse_target;

    if ($(mouseTarget).is('.sticker') || $(mouseTarget).is('.table')) {
      mouseTarget = $(opts.event.target).parents('g.textarea')[0];
    }

    if ($(mouseTarget).is('#selectorParentGroup')) {
      const id = ($(mouseTarget).find('#selectorGroup0').attr('data-element-id'));

      if (id && INKIVE.DesignFacade.get(id).type === 'text') {
        mouseTarget = $('#' + id)[0];
      }
    }

    // Start text edit
    if (canvas.getMode() === 'textedit') {
      selectWord(opts);
    } else if (mouseTarget && mouseTarget.tagName === 'g' && $(mouseTarget).is('.textarea')) {
      const isTableText = mouseTarget?.parentElement?.classList?.contains?.('table-cell');
      const tlist = canvas.getTransformList(isTableText ? mouseTarget.closest('.element.table') : mouseTarget);

      for (let i = 0; i < tlist.numberOfItems; i++) {
        const transform = tlist.getItem(i);
        const isRotationType = transform.type === SVGTransform.SVG_TRANSFORM_ROTATE || transform.type === SVGTransform.SVG_TRANSFORM_MATRIX;

        if (!isRotationType) continue;
      }

      const permissions = INKIVE.RestrictionsFacade.getAllElementPermissions(mouseTarget.getAttribute('id'));
      const isTextTypingSupported = checkTextTypingSupported(permissions);

      function checkTextTypingSupported (permissions) {
        const textTypingSupportedPermissions = ['basic', 'colors', 'highlightcolors', 'colors-library', 'highlightcolors-library', 'text_predefined_content', 'character_number'];

        return permissions.some(permission => {
          const keys = Object.keys(permission);
          return keys.some(key => key && !textTypingSupportedPermissions.includes(key));
        });
      }

      const isAllowedRestricted = (() => {
        if (isTableText) {
          const tableCell = mouseTarget.parentElement;
          const tableRow = tableCell && tableCell.parentElement;
          const table = tableRow && tableRow.parentElement;
          return INKIVE.RestrictionsFacade.isActionAllowed('table_content', table);
        }

        const defaultPermissions = INKIVE.RestrictionsFacade.getAllDefaultPermissions();
        const restrictions = permissions.length && permissions[0];
        const isTextTypingAllowed = INKIVE.RestrictionsFacade.isTextTypingAllowed(restrictions, defaultPermissions);
        return isTextTypingAllowed;
      })();

      if (isAllowedRestricted) {
        if (INKIVE.C.IS_MOBILE || isTextTypingSupported) {
          beginTextTyping({ ...opts, elem: mouseTarget, permissions });
        } else {
          beginTextEditing({ ...opts, elem: mouseTarget });
        }
      }

      // TEMP measuring
      !INKIVE.C.IS_DEV && !INKIVE.C.IS_MOBILE && typeof ga === 'function' && ga('send', {
        hitType: 'event',
        eventCategory: 'Editor UI | Popupmenu | text',
        eventAction: 'double_click'
      });
    }

    return false;
  }

  return {
    updateSelection: function (options) {
      if (currentTextInstance && canvas.getMode() === 'textedit') {
        updateSelection(options.previousStart, options.typing);
      }
    },

    selectionRange: function () {
      let selectionStart = 0;
      let selectionEnd = 0;

      if (canvas.getMode() === 'textedit') {
        !INKIVE.C.IS_MOBILE_APP && currentTextInputElementLastFocus && currentTextInputElementLastFocus.focus();

        if (currentTextInputElement) {
          selectionEnd = currentTextInputElement.selectionEnd;

          if (currentTextInputElement.selectionStart !== 0) {
            selectionStart = currentTextInputElement.selectionStart;
          }
        }
      }

      return {
        start: selectionStart,
        end: selectionEnd
      };
    },

    /**
     * Edit text on DBL CLICK
     */
    dblClick: function (opts) {
      const isList = opts.event.target.classList.contains('bullets') || opts.event.target.classList.contains('ordered');
      if (canvas.getMode() === 'textedit' && isList) {
        selectWord(opts, true);
        const bbox = opts.event.target.getBBox();
        setCursorByBBox(bbox);
        setSectionByBBox(bbox);
        return;
      }

      return ifEditText(opts, this.beginTextTyping, this.beginTextEditing);
    },

    mouseDown: function (opts) {
      mouseMoving = false;
      const mouseX = opts.mouse_x;
      const mouseY = opts.mouse_y;
      let mouseTarget = opts.mouse_target;
      const prevElemDoubleTap = opts.prevElemDoubleTap;
      const selectedElements = opts.selectedElements;
      const isTextEdit = canvas.getMode() === 'textedit';
      const previousElement = opts.prevElements && opts.prevElements.length === 1 ? opts.prevElements[0] : null;

      if (prevElemDoubleTap && mouseTarget !== prevElemDoubleTap) {
        return;
      }

      const shouldEndTextEdit = mouseTarget !== selectedElements[0] && !mouseTarget?.querySelector('#text_cursor') && isTextEdit;
      if (shouldEndTextEdit) {
        this.endEditing();
        const isTableSelected = selectedElements[0]?.closest('.table');
        const isMouseTargetTable = mouseTarget?.classList?.contains('table');
        const isTargetTableCell = opts.event.target?.closest('.table-cell');
        (isTargetTableCell || isTableSelected) && canvas.clearSelection();

        if (isMouseTargetTable && isTargetTableCell) {
          canvas.addToSelection([mouseTarget], false);
          INKIVE.Table.beginTableEditingMode(mouseTarget, opts?.event);
        }

        return;
      }

      if ($(mouseTarget).is('.sticker') || $(mouseTarget).is('.table')) {
        mouseTarget = $(opts.event.target).parents('g.textarea')[0];
      }
      isMouseDown = true;

      if ((previousElement && previousElement !== mouseTarget) || selectedElements.length > 1) timesClickedOnElement = 0;

      initCache(mouseTarget);

      if (isTextEdit) {
        currentTextInputElement.focus();
        updateCursorPosition(opts);

        if (currentTextInstance) {
          const bb = currentTextInstance.getBBox();
          const zoom = canvas.getZoom();

          bb.x = bb.x * zoom;
          bb.y = bb.y * zoom;
          bb.width = bb.width * zoom;
          bb.height = bb.height * zoom;

          if (!(mouseX < bb.x + bb.width && mouseX > bb.x && mouseY > bb.y && mouseY < bb.y + bb.height)) {
            this.endEditing();

            return {
              started: false,
              keep: false
            };
          }
        }

        return { started: true };
      }
    },

    mouseUp: function (opts) {
      let mouseTarget = opts.mouse_target;

      if ($(mouseTarget).is('.sticker') || $(mouseTarget).is('.table')) {
        mouseTarget = $(opts.event.target).parents('g.textarea')[0];
      }

      const isTableText = mouseTarget?.parentElement?.classList?.contains?.('table-cell');

      isMouseDown = false;

      if (canvas.getMode() === 'textedit' && currentTextInstance) {
        const threeOrMoreClicks = opts.event.originalEvent.detail >= 3;
        const tlist = canvas.getTransformList(mouseTarget);
        const isRotated = tlist.numberOfItems === 1 &&
          (tlist.getItem(0).type === SVGTransform.SVG_TRANSFORM_ROTATE || tlist.getItem(0).type === SVGTransform.SVG_TRANSFORM_MATRIX);
        if (isTableText && isRotated) {
          clear();
          this.beginTextTyping({ ...opts, elem: mouseTarget });
        }

        if (threeOrMoreClicks && !isRotated) return selectAll();

        // Finish text edit if something
        return {
          started: false,
          keep: false
        };
      } else if (canvas.getMode() === 'select') {
        // clear cache
        currentTextInstance = null;

        const selectedElements = opts.selectedElements;
        const isTextarea = mouseTarget && mouseTarget.tagName === 'g' && $(mouseTarget).is('.textarea');
        if (selectedElements.length && isTextarea && mouseTarget === selectedElements[0]) {
          timesClickedOnElement++;
          if (timesClickedOnElement > 1 && !mouseMoving) {
            timesClickedOnElement = 0;
            return ifEditText(opts, this.beginTextTyping, this.beginTextEditing);
          }
        } else {
          timesClickedOnElement = 0;
        }
      }
    },

    resizeText: function (opts) {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(opts.elem);

      let scale = opts.scale || getNewScale();
      scale = parseFloat(scale.toFixed(2));

      if (currentTextInstance.original_font_size) {
        currentTextInstance.original_font_size = currentTextInstance.original_font_size * scale;
      }

      currentTextInstance.text.font_size = Number(currentTextInstance.text.font_size) * scale;
      currentTextInstance.text.vertical_offset = Number(currentTextInstance.text.vertical_offset) * scale;
      currentTextInstance.text.stroke_width = Number(currentTextInstance.text.stroke_width) * scale;

      if (currentTextInstance.text.auto_adjustable) {
        currentTextInstance.text.original_font_size = currentTextInstance.text.font_size;
        currentTextInstance.text.changing_font_size = true;
      }

      _.each(currentTextInstance.text.styles, function (style) {
        if (style.attribute === 'font-size') {
          style.value *= scale;
        }
      });

      const textAttributesToResize = ['paragraph_spacing', 'line_spacing', 'letter_spacing', 'line_margin', 'first_line_margin', 'right_margin', 'word_spacing'];
      const stringAttributesToResize = ['bulletWidth', 'bulletFontSize', 'orderedWidth', 'orderedFontSize', 'letterSpacing', 'lineMargin', 'line_margin', 'paragraph_spacing', 'first_line_margin', 'right_margin', 'line_spacing'];

      for (const attribute of textAttributesToResize) {
        const currentValue = Array.isArray(currentTextInstance.text[attribute])
          ? currentTextInstance.text[attribute]
          : Number(currentTextInstance.text[attribute]);

        if (!currentValue) continue;
        currentTextInstance.text[attribute] = Array.isArray(currentTextInstance.text[attribute])
          ? currentValue.map(value => value * scale)
          : currentValue * scale;
      }

      for (const attribute of stringAttributesToResize) {
        for (const string of currentTextInstance.text.strings) {
          const currentValue = Number(string[attribute]);

          if (!currentValue) continue;
          string[attribute] = currentValue * scale;
        }
      }

      if (opts.isTableCell) {
        currentTextInstance.render(canvas, { allowResizeBelowMinValue: true });
        return;
      }

      // Trigger an event so the text toolbar can be updated
      INKIVE.EditorFacade.trigger('text.resized', { newFontSize: currentTextInstance.text.font_size });

      currentTextInstance.render(canvas);
      currentTextInstance = null;

      function getNewScale () {
        const bb = currentTextInstance.text.getBBox();
        const rectbb = currentTextInstance.getBBox();

        const rectbbHeight = rectbb.height;
        const bbHeight = bb.height || rectbbHeight;

        return rectbbHeight / bbHeight;
      }
    },

    mouseMove: function (opts) {
      mouseMoving = true;
      switch (canvas.getMode()) {
        case 'textedit':
          opts.isMoveCursor = isMouseDown;
          updateCursorPosition(opts);

          break;
      }
    },

    startResize: function (opts) {
      if (opts && opts.elem && $(opts.elem).is('.textarea')) {
        canvas.current_resize_mode === 'e' && canvas.call('advice.show', 'resize-text-adjust-to-box');
      }
    },

    endResize: function () {
      if (currentTextInstance) {
        this.resizeText({ scale: 1, elem: currentTextInstance.dom });
      }
    },

    beginTextTyping: function (opts) {
      opts.isTypewritter = true;
      $(opts.elem).is('.subelement') && $(opts.elem).addClass('active');
      toggleHeaderButtons(true);
      startTextEditing(opts) && showTextForm(opts);
    },

    beginTextEditing: function (opts) {
      if (startTextEditing(opts)) {
        initWeb();
        opts.beginEditing = true;
        updateCursorPosition(opts);

        currentTextInputElement && currentTextInputElement.focus();
      }
    },

    endEditing: function () {
      currentTextInstance && currentTextInstance.invalidate();
      currentTextInputElement && currentTextInputElement.blur();
      canvas.setMode('select');
      timesClickedOnElement = 0;
    },

    // Mode changed
    clear: function () {
      if (canvas.getMode() !== 'textedit') {
        clear();
      }
    },

    zoomChanged: function () {
    },

    renderTextArea: function (opts) {
      if (opts && opts.textArea) {
        opts.textArea.render(canvas);
        if (opts.isCursor !== false) currentTextInstance = opts.textArea;
        updateSelection(null, true);
      }
    },

    insertContent: function (opts) {
      currentTextInstance = new INKIVE.Text.Textarea().setDOM(opts.elem, false);

      const initialStart = currentTextInputElement.selectionStart;
      const initialEnd = currentTextInputElement.selectionEnd;

      const currentContent = currentTextInputElement.value;
      const updatedContent = currentContent.substr(0, initialStart) + opts.content + currentContent.substr(initialEnd);

      currentTextInputElement.value = updatedContent;
      currentTextInputElement.selectionStart = initialStart + opts.content.length;
      currentTextInputElement.selectionEnd = initialStart + opts.content.length;

      INKIVE.DesignFacade.set({
        textarea: currentTextInstance,
        text_content: updatedContent,
        cursor_position: currentTextInputElement.selectionStart
      });

      if (opts.smart_asset) {
        currentTextInstance.setValues({
          smart_asset: null
        });
        currentTextInstance.text.setPartialStyling(initialStart, initialStart + opts.content.length, 'data-defined-placeholder', opts.smart_asset, { skipHistory: true, shouldRemoveOverlappedStyles: true });
        currentTextInstance.text.setValues({
          shouldRecalculateStyle: false
        });

        currentTextInstance.render(canvas);
        INKIVE.DesignFacade.trigger('text.update');
      } else if (opts.shouldRemoveOverlappedStyles) {
        currentTextInstance.text.setValues({
          shouldRecalculateStyle: false
        });
        currentTextInstance.text.removeSpecificPartialStyling(initialStart, initialStart + opts.content.length, 'data-defined-placeholder', undefined, { skipHistory: true, shouldRemoveOverlappedStyles: true });

        currentTextInstance.render(canvas);
        INKIVE.DesignFacade.trigger('text.update');
      }
      updateSelection(initialStart, true);
    },

    setSelection: function (opts) {
      if (!currentTextInstance) {
        initCache(opts.element);
      }

      const realStart = currentTextInstance.text.realCharIndexToFake(opts.start);
      const realEnd = currentTextInstance.text.realCharIndexToFake(opts.end);

      currentTextInputElement.setSelectionRange(realStart, realEnd);
      canvas.setMode('textedit');
      updateSelection();
    }
  };
};
